import { useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ArrowLeftToLine } from 'lucide-react';

import { useGetCaseForGrade } from '@/hooks/api/use-get-case-for-grade';
import { useGradeFlow } from '@/states/grade-flow';
import { ROUTES } from '@/router/routes';
import { parseDateString } from '@/utils/dates';

import { Section } from '@/components/content-layout/section';
import { SectionHeader } from '@/components/content-layout/section-header';
import { FetchStates } from '@/components/fetch-states';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { DrInsightsFlow } from '@/components/insights/dr-insights-flow';
import { LabelValue } from '@/components/ui/label-value';

export function DrInsightsPage() {
  const { t } = useTranslation();
  const { caseId } = useParams<{ caseId: string }>();

  const { data: _case, loading, error } = useGetCaseForGrade({ caseId });

  const { initializeFlow, setIsReadable, setCaseValidClaimed, reset } = useGradeFlow(
    useShallow((s) => ({
      initializeFlow: s.initializeFlow,
      setIsReadable: s.setIsReadable,
      setCaseValidClaimed: s.setCaseValidClaimed,
      reset: s.reset,
    }))
  );

  useEffect(() => {
    if (_case) {
      initializeFlow(_case);

      setIsReadable(true);

      setCaseValidClaimed(true);
    }
  }, [_case, initializeFlow, setIsReadable, setCaseValidClaimed]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const formatDate = useCallback(
    (date: string) => parseDateString(date)?.toLocaleDateString() ?? 'Invalid Date',
    []
  );

  if (loading) {
    return (
      <Section>
        <SectionHeader title={t('insights-dr-grading-title', 'DR Insights Grading')} element="h1" />
        <FetchStates loading />
      </Section>
    );
  }

  if (error ?? (!_case || !caseId)) {
    return (
      <Section>
        <SectionHeader title={t('insights-dr-grading-title', 'DR Insights Grading')} element="h1">
          <Link to={ROUTES.INSIGHTS}>
            <Button size="icon" variant="ghost">
              <ArrowLeftToLine />
            </Button>
          </Link>
        </SectionHeader>
        <Alert type="error" title={t('error-loading-case', 'Error Loading Case')}>
          {!caseId
            ? t('case-id-missing', 'Case ID is missing from the URL.')
            : (error?.message ??
              t('case-not-found', 'Case not found or you do not have access to it.'))}
        </Alert>
      </Section>
    );
  }

  return (
    <Section>
      <SectionHeader title={t('insights-dr-grading-title', 'DR Insights Grading')} element="h1">
        <div className="inline-flex-full-center gap-4">
          <div className="inline-flex gap-8">
            <LabelValue label="User Email" value={_case?.user_email} />
            <LabelValue label="Created At" value={formatDate(_case?.date_created ?? '')} />
          </div>
          <Link to={ROUTES.INSIGHTS}>
            {' '}
            <Button size="icon" variant="ghost">
              <ArrowLeftToLine />
            </Button>
          </Link>
        </div>
      </SectionHeader>

      <DrInsightsFlow selectedCase={_case} />
    </Section>
  );
}
